import React, { useState } from 'react';
import { X, BookOpen, Zap, Shield, Search, Share2, FileJson } from 'lucide-react';

interface GuideDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const GuideDialog: React.FC<GuideDialogProps> = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState<'start' | 'features' | 'tips'>('start');

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <BookOpen className="text-blue-600 dark:text-blue-400" />
                        Fibre User Guide
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setActiveTab('start')}
                        className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'start'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                    >
                        Quick Start
                    </button>
                    <button
                        onClick={() => setActiveTab('features')}
                        className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'features'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                    >
                        Key Features
                    </button>
                    <button
                        onClick={() => setActiveTab('tips')}
                        className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'tips'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                    >
                        Pro Tips
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                    {activeTab === 'start' && (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Getting Started with Fibre</h3>
                            <p>
                                Fibre is your digital investigation board. Here's the basic workflow:
                            </p>
                            <ol className="list-decimal pl-5 space-y-3 marker:text-blue-500">
                                <li>
                                    <strong>Create a Board:</strong> Click "New Board" on the dashboard to start a fresh investigation.
                                </li>
                                <li>
                                    <strong>Add Entities (Nodes):</strong> Right-click anywhere on the canvas to add People, Evidence, Locations, or Events.
                                </li>
                                <li>
                                    <strong>Connect Them (Edges):</strong> Drag from the handle (dots) of one node to another to create a relationship.
                                </li>
                                <li>
                                    <strong>Save & Export:</strong> Your work saves automatically. Use the "Export Case Report" button to create an offline backup that includes all images.
                                </li>
                            </ol>
                        </div>
                    )}

                    {activeTab === 'features' && (
                        <div className="space-y-6">
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 mt-1">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                                        <Search size={18} />
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 dark:text-white">Timeline View</h4>
                                    <p className="text-sm mt-1">
                                        Every event with a date automatically appears on the bottom Timeline. Scrub through time to replay the sequence of events.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="flex-shrink-0 mt-1">
                                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                                        <Shield size={18} />
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 dark:text-white">Secure Profiles</h4>
                                    <p className="text-sm mt-1">
                                        Enable Two-Factor Authentication (2FA) in your dashboard to secure your account and investigations.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="flex-shrink-0 mt-1">
                                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                                        <Share2 size={18} />
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 dark:text-white">Case Reports</h4>
                                    <p className="text-sm mt-1">
                                        The "Export Case Report" button creates a single, self-contained file with all evidence embedded. These are "Case Ready" files perfect for offline archiving or submitting as evidence.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="flex-shrink-0 mt-1">
                                    <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
                                        <FileJson size={18} />
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 dark:text-white">JSON Backup & Sharing</h4>
                                    <p className="text-sm mt-1">
                                        Use "Export JSON" to save a raw data file of your board. Send this file to another investigator, and they can use "Import JSON" to load your exact investigation board on their machine.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'tips' && (
                        <div className="space-y-4">
                            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                <h4 className="font-bold text-yellow-800 dark:text-yellow-400 flex items-center gap-2">
                                    <Zap size={16} /> Use Keyboard Shortcuts
                                </h4>
                                <ul className="mt-2 text-sm space-y-1 text-yellow-700 dark:text-yellow-300">
                                    <li>• <strong>Backspace / Delete:</strong> Remove selected nodes or edges</li>
                                    <li>• <strong>Ctrl + Z:</strong> Undo action</li>
                                    <li>• <strong>Ctrl + Y:</strong> Redo action</li>
                                    <li>• <strong>Scroll Wheel:</strong> Zoom in/out</li>
                                </ul>
                            </div>

                            <h4 className="font-bold text-gray-900 dark:text-white mt-4">Advanced Interactions</h4>
                            <ul className="list-disc pl-5 space-y-2 text-sm">
                                <li>Double-click a node to edit its label instantly.</li>
                                <li>Right-click a node or edge to see more options (color, type, notes).</li>
                                <li>Drag an image file directly onto a node to upload it as a profile picture.</li>
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GuideDialog;
